// appBatches — the batch list and a single batch page (P03, canvas 4 "Batch 100").
// Keys mirror ../pl/appBatches.ts exactly. See that file for why these 33 strings
// moved out of `lib/tFallback` and what the count-bearing ones are shaped around.
export const appBatches = {
  // Segment above the list (0016/B2, minimum until package 5): Series = this screen, All edits = /library.
  mode: { label: 'Mode', series: 'Series', all: 'All edits' },
  // Whole-series download label. Rule 8 removes explanatory ZIP-state sentences;
  // the enabled state of the action carries that information.
  archive: {
    download: 'Download all ({n})',
    nothingYet: 'Nothing has finished rendering yet.',
    tooLarge: 'This series is too large to send as one file - download the edits individually.',
    sizeUnknown: 'We cannot size this archive right now - download the edits individually.',
  },
  autopilot: {
    start: 'Autopilot',
    needsOne: 'Autopilot needs at least one finished edit.',
  },
  loading: 'Loading series…',
  error: 'Could not load your series.',
  retry: 'Try again',

  // ── R5 (20.09, rule 30): THREE words for a render state ────────────────────
  // Queue and render are ONE word ("in queue"); only the progress bar on the tile
  // tells them apart. "rendering" and "failed" leave the chips, pills and filters.
  // The status of the whole batch. `partial` exists because "ready" would be
  // untrue while some renders failed; `running` says the same as `queued`, because
  // in the product dictionary the queue and the render are one state.
  status: {
    queued: 'in queue',
    running: 'in queue',
    done: 'ready',
    partial: 'partly ready',
    failed: 'error',
    empty: 'empty',
  },

  // ── Rule 59 (23.09): a scheduled or published edit wears a publication pill ──
  // The word on the tile and in the preview's top line; with both states, "published"
  // (the heavier one wins). The account count stands only in the preview line, as data.
  publication: {
    published: 'published',
    scheduled: 'scheduled',
    accounts_one: '{n} account',
    accounts_few: '{n} accounts',
    accounts_many: '{n} accounts',
  },

  filters: {
    ready: 'Ready',
    queued: 'In queue',
    failed: 'Error',
  },

  card: {
    // Not a standalone name — the component joins "{untitled} · {date}"
    // (`batchTitle`), so the date is already on the card and we are not
    // pretending someone named this batch.
    untitled: 'Series',
    ready: '{done} of {total} ready',
    // R5, rule 30: the series pill counts ERRORS. English has two forms where
    // Polish has three; `few` and `many` hold the same string (plural.ts).
    failed: '{n} errors',
    failed_one: '{n} error',
    failed_few: '{n} errors',
    failed_many: '{n} errors',
    open: 'Open series',
    // Session 19, decision 16: a finished series the user has not looked at yet.
    // The pill fades on the first interaction with the CARD (hover ≥ 600 ms, focus,
    // touch, a download, opening the series or a preview) — landing on /edits only
    // clears the navigation check, which is a different promise.
    fresh: 'New',
  },

  detail: {
    title: 'Series - edits: {n}',
    loading: 'Loading series…',
    error: 'This series could not be loaded.',
    notFound: {
      title: 'No such series',
      body: 'This series does not exist or does not belong to your account.',
      back: 'Back to Edits',
    },
    ready: 'edits ready',
  },

  job: {
    queued: 'Queued',
    rendering: 'Queued',
    done: 'Ready',
    failed: 'Error',
    download: 'Download',
    retry: 'Try again',
    // {n} = GEMS, not an attempt count. The price sits on the button because a
    // retry charges again, and the user should know that BEFORE clicking.
    // English needs two forms where Polish needs three; `few` and `many` hold the
    // same string so the key SETS stay identical across languages (plural.ts).
    retryCost: 'Try again ({n} gems)',
    retryCost_one: 'Try again ({n} gem)',
    retryCost_few: 'Try again ({n} gems)',
    retryCost_many: 'Try again ({n} gems)',
    reason: {
      renderFailed: 'The render did not finish',
      // An unknown error class says the same as a known one, because the
      // consequence for the user is identical: no video, gems back. A raw code
      // would not help.
      unknown: 'The render did not finish',
    },
  },

  empty: {
    title: 'Nothing here yet',
    cta: 'Make your first series',
  },
}
